// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import RemoveAll from "@flip/XDL/Icons/RemoveAll";
import { fontConfig } from "@flip/XDL/PreStyle/typography";
import { color, ellipsis, styledScrollbar, xScreen } from "@flip/XDL/Utilities";
import { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

interface MiniCartLineStyles {
    cartLineWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    cartLineImage?: LazyImageProps;
    detailsWrapper?: InjectableCss;
    closeWrapper?: InjectableCss;
    brandWrapper?: InjectableCss;
    brandLink?: LinkPresentationProps;
    removeCartLineLink?: LinkPresentationProps;
    lineDescription?: LinkPresentationProps;
    partNumbers?: {
        wrapper: InjectableCss;
    };
    qtyAndPriceWrapper?: InjectableCss;
    qtyWrapper?: InjectableCss;
    priceWrapper?: InjectableCss;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    errorMessagesWrapper?: InjectableCss;
    // XNG-Changes
    labelText?: TypographyProps;
    legacyManufacturerWrapper?: InjectableCss;
}

// cart lines style
export const miniCartLineStyles: MiniCartLineStyles = {
    cartLineWrapper: {
        css: css`
            display: flex;
            position: relative;
            margin-bottom: 22px;
        `,
    },
    imageWrapper: {
        css: css`
            height: 92px;
            width: 92px;
            overflow: hidden;
            margin-right: 20px;
            flex-shrink: 0;
        `,
    },
    cartLineImage: {
        width: "92px",
        height: "92px",
        css: css`
            max-height: 100%;
            max-width: 100%;
            img {
                height: auto;
                max-height: 100%;
                max-width: 100%;
                width: auto;
            }
        `,
    },
    detailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;
        `,
    },
    brandWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            position: relative;
            margin-bottom: 4px;
        `,
    },
    // XNG-Changes: FLIP-507
    brandLink: {
        typographyProps: {
            size: 12,
            weight: fontConfig.weight.semiBold,
            lineHeight: "13px",
            css: css`
                ${ellipsis(1)}
            `,
        },
        css: css`
            ${({ isAuthorized }: any) =>
                !isAuthorized &&
                css`
                    &&& {
                        ${TypographyStyle} {
                            color: ${color("text")};
                            &:visited {
                                color: ${color("text")};
                            }
                            &:hover {
                                color: ${color("secondary")};
                            }
                        }
                    }
                `}
        `,
    },
    removeCartLineLink: {
        icon: {
            iconProps: {
                src: RemoveAll,
                size: 10,
                color: "text.main",
                css: css`
                    margin-right: 10px;
                    &&&:hover {
                        color: ${color("common.ui5")};
                    }
                `,
            },
        },
    },
    lineDescription: {
        typographyProps: {
            size: 14,
            weight: 700,
            lineHeight: "16px",
            css: css`
                letter-spacing: 0;
                min-height: 32px;
                ${ellipsis(2)}

                &&& {
                    color: ${color("text.main")};
                }
            `,
        },
        css: css`
            margin-bottom: 4px;
            max-width: 294px;
        `,
    },
    partNumbers: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                row-gap: 4px;
                margin-bottom: 4px;
            `,
        },
    },
    qtyAndPriceWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            justify-content: space-between;
            margin-top: 12px;
        `,
    },
    qtyWrapper: {
        css: css`
            margin-right: 5px;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
        `,
    },
    priceWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justity-content: flex-start;
            width: 50%;
        `,
    },
    cartLineQuantityStyles: {
        editableQuantityTextField: {
            sizeVariant: "small",
            labelProps: {
                css: css`
                    display: none;
                `,
            },
            cssOverrides: {
                formField: css`
                    width: auto;
                `,
                inputSelect: css`
                    width: 74px;
                    text-align: center;
                    font-size: 13px;
                    font-weight: 600;
                    letter-spacing: 0;
                    line-height: 18px;
                `,
            },
        },
    },
    errorMessagesWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
            & > div:not(:last-child) {
                margin-bottom: 15px;
            }
        `,
    },
    labelText: {
        size: 12,
        weight: fontConfig.weight.semiBold,
        transform: "capitalize",
        color: "text",
        css: css`
            letter-spacing: 0;
            line-height: 13px;
            margin-right: 4px;
            flex-shrink: 0;
            display: inline-block;

            &:after {
                content: ":";
            }
        `,
    },
    legacyManufacturerWrapper:{
        css: css`
            display: flex;
        `
    }
};
